import { RiPencilLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { AgButton, AgSwitch } from 'src/components/ui';
import { GridColObj } from 'src/modules/grids';
import { appLinks } from 'src/routing/appLinks';
import { dateCellFormatter, dateGetter } from 'src/utils/grid/cell/cellFormatters';

import { applyToLabels, PricingSheetMeta, pricingFormulaLabels, Status, statusClass, statuses } from '../constants';
import { getPricingSheetApplyTo, getPricingSheetStatus } from '../utils';

import { useUpdatePricingSheet } from './useUpdatePricingSheet';

export default function usePricingSheetsColumns(): GridColObj {
  const navigate = useNavigate();
  const { update: updatePricingSheet } = useUpdatePricingSheet();

  return useMemo(
    () => ({
      name: { headerName: 'Name', type: 'string', minWidth: 300 },
      // Computed cells: the server filters them by label (pricingSheets.repo.ts) but cannot sort them.
      status: {
        headerName: 'Status',
        type: 'string',
        sortable: false,
        valueGetter: (_value, row) => getPricingSheetStatus(row as PricingSheetMeta),
        renderCell: ({ value }) => <span className={`badge ${statusClass[value as Status]}`}>{value}</span>,
        getFilterSelectOptions: [...statuses],
        width: 200,
      },
      applyTo: {
        headerName: 'Apply to',
        type: 'string',
        sortable: false,
        valueGetter: (_value, row) => getPricingSheetApplyTo(row as PricingSheetMeta),
        getFilterSelectOptions: [...applyToLabels],
      },
      pricingFormula: {
        headerName: 'Pricing model',
        type: 'string',
        valueFormatter: (value) => pricingFormulaLabels[value],
        getFilterSelectOptions: Object.entries(pricingFormulaLabels).map(([value, label]) => ({ value, label })),
      },
      startDate: {
        headerName: 'Start date',
        type: 'date',
        valueGetter: (value) => dateGetter(value),
        valueFormatter: dateCellFormatter,
      },
      expiryDate: {
        headerName: 'Expiry date',
        type: 'date',
        flex: 0.5,
        valueGetter: (value) => dateGetter(value),
        valueFormatter: dateCellFormatter,
      },
      actions: {
        headerName: '',
        disableExport: true,
        resizable: false,
        sortable: false,
        filterable: false,
        minWidth: 100,
        width: 100,
        renderCell: ({ row }) => {
          const sheet = row as PricingSheetMeta;
          return (
            <div className="flex items-center gap-1">
              <AgSwitch
                isSelected={sheet.enabled}
                onChange={() => updatePricingSheet({ id: sheet.id, input: { enabled: !sheet.enabled } })}
              />
              <AgButton
                variant="ghost"
                size="icon-sm"
                shape="circle"
                onPress={() => void navigate(appLinks.pricingSheet({ id: sheet.id }))}
              >
                <RiPencilLine size={20} />
              </AgButton>
            </div>
          );
        },
      },
    }),
    [updatePricingSheet, navigate]
  );
}
